import { Button, Divider, Col, Row, Avatar } from '@pankod/refine-antd'
import { formatScorecardDecimal } from 'helpers/scorecardFormat'
import { css } from '@emotion/react'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from '@pankod/refine-react-router-v6'
import { UserOutlined } from '@ant-design/icons'
// import { useReactToPrint } from 'react-to-print'
import SectionWiseChart from 'components/chart/SectionWiseChart'
import { SectionTagChart } from 'components/chart/SectionTagsChart'
import letterheadTop from 'assets/abstract blue wave letterhead vector_Top.png'
import letterEnd from 'assets/abstract blue wave letterhead vector_8626181.png'
import eligibleIcon from 'assets/safety.png'
import noteligibleIcon from 'assets/security.png'
import scoreIconImg from 'assets/aim.png'
import graphIconImg from 'assets/line-chart.png'
import pageHeader from 'assets/pageheader-removebg-preview.png'
import pageFooter from 'assets/detailsaddedfooter-removebg-preview.png'
import qualifiedIconImg from 'assets/pngwing.com (4).png'
import centaTqLogo from 'assets/tq_centa.png'
import centaSktLogo from 'assets/centaR-removebg-preview.png'
import centaCIcon from 'assets/centa_C_logo.png'
import linkPng from 'assets/linkPng.png'
import Notfound from 'assets/404 error with people holding the numbers-rafiki.png'
import NoData from 'assets/No_Result.png'
import ProtectedFile from 'assets/protected_file.png'
import patternCoverPage from 'assets/jhk-cover-page-design.png'
import previewLogo from 'assets/CENTA_PREVIEW_IMG.webp'
import './index.less'
import { findCandidateReportDetails, findEvaluatonDetailsByCandidateId, findPaperTotalMarksWithEvaluationId } from 'gql/query'
import { nhost } from 'utility/nhost'
import { changeDateFormat, getDateTwoYearsFromNow } from 'helpers/reportDataGenerating'
import { handleSectionPriority } from 'helpers/candidateReport/sectionPriority'
import { getLearningRecommendation } from 'utility/learning_recommendations'

interface Props {
  candidate?: string
  preview?: boolean
}

const SCORECARD_WIDTH_PX = 794
const MOBILE_BREAKPOINT_PX = 768
const MOBILE_PDF_WIDTH_PX = 414

const isPdfDownloadPage = () => window.location.pathname.includes('/download/scorecard-pdf')

// Unreleased (hidden) or ineligible results are shown only to signed-in AMS staff. Everyone else -
// public scorecard links, the PDF download page and server-side PDF captures - gets the
// "under review" screen, so sharing a link cannot bypass the visibility toggle.
const isStaffViewer = async () => {
  try {
    return await nhost.auth.isAuthenticatedAsync()
  } catch {
    return false
  }
}

const isMobilePdfMode = () => {
  if (isPdfDownloadPage()) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  return params.get('mobilePdf') === '1'
}

const renderOptionFlagMessage = (optionFlagDetails?: {
  percentageOfCloseToCorrect?: number
  percentageOfMostWrong?: number
  percentageOfBlankResponse?: number
}) => {
  const percentageOfCloseToCorrect = Number(optionFlagDetails?.percentageOfCloseToCorrect ?? 0)
  const percentageOfMostWrong = Number(optionFlagDetails?.percentageOfMostWrong ?? 0)
  const percentageOfBlankResponse = Number(optionFlagDetails?.percentageOfBlankResponse ?? 0)

  if (
    percentageOfCloseToCorrect > percentageOfMostWrong &&
    percentageOfCloseToCorrect > percentageOfBlankResponse
  ) {
    return (
      <h4>
        Your test report shows that you provided{' '}
        <span>{`${formatScorecardDecimal(percentageOfCloseToCorrect)} % of close to correct response `}</span>
        in comparison to <span>{`${formatScorecardDecimal(percentageOfMostWrong)} % of`}</span> most wrong responses
      </h4>
    )
  }

  if (percentageOfCloseToCorrect === 0 && percentageOfMostWrong === 0 && percentageOfBlankResponse === 0) {
    return <h4>Impressive job ! You have answered all correct.</h4>
  }

  if (
    percentageOfMostWrong > percentageOfCloseToCorrect &&
    percentageOfMostWrong > percentageOfBlankResponse
  ) {
    return (
      <h4>
        Your test report indicates that you selected{' '}
        <span>{`${formatScorecardDecimal(percentageOfCloseToCorrect)} % of close to correct response, `}</span>
        but there were <span>{`${formatScorecardDecimal(percentageOfMostWrong)} % of`}</span> most wrong responses chosen.
      </h4>
    )
  }

  return (
    <h4>
      Your test report indicates that{' '}
      {percentageOfCloseToCorrect === 0 ? (
        <span>you had no close-to-correct responses, </span>
      ) : (
        <span>{`${formatScorecardDecimal(percentageOfCloseToCorrect)} % of your responses were close to correct, `}</span>
      )}
      and <span>{`${formatScorecardDecimal(percentageOfBlankResponse)} %`}</span> were wrong responses.
    </h4>
  )
}

const getResultPercentage = (totalMarks?: number | string, totalPaperScore?: number | string) => {
  const obtained = Number(totalMarks ?? 0)
  const total = Number(totalPaperScore ?? 0)
  if (!total || total <= 0) {
    return 0
  }
  return (obtained / total) * 100
}

const CentaCandidateReport: React.FC<Props> = ({ candidate, preview = false }) => {
  const [allowHiddenResults, setAllowHiddenResults] = useState<boolean>(false)
  const componentPdf = useRef<HTMLDivElement | null>(null)
  const [loading, setLoading] = useState<boolean>(Boolean(candidate))
  const [resultData, setResultData] = useState<any>()
  const [validCandidate, setValidCandidate] = useState<boolean>(true)
  const [candidateName, setCandidateName] = useState('')
  const [testDates, setTestDates] = useState<any>({})
  const [modileView, setModileView] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/download/scorecard-pdf')) {
      return false
    }
    return window.innerWidth < MOBILE_BREAKPOINT_PX
  })
  const [mobileScale, setMobileScale] = useState(1)
  const mobileLayoutRef = useRef({ isMobile: window.innerWidth < MOBILE_BREAKPOINT_PX, scale: 1 })
  const [learningResourcesList, setLearningResourceList] = useState([])
  const [sktError, setSktError] = useState<boolean>(false)
  const [sktResultDetails, setSktResultDetails] = useState<any>()
  const [digitalBadgeContentHasToChange, setDigitalBadgeContentHasToChange] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const sectionBottomValue = 30
  const tagBottomValue = 20
  const candidateTotalResultPercentage = getResultPercentage(
    resultData?.section_level_details?.total_marks,
    resultData?.section_level_details?.total_paper_score
  )
  const sktTotalResultPercentage = getResultPercentage(
    sktResultDetails?.section_level_details?.total_marks,
    sktResultDetails?.section_level_details?.total_paper_score
  )

  const updateMobileLayout = useCallback(() => {
    if (isPdfDownloadPage()) {
      if (mobileLayoutRef.current.isMobile || mobileLayoutRef.current.scale !== 1) {
        mobileLayoutRef.current = { isMobile: false, scale: 1 }
        setModileView(false)
        setMobileScale(1)
      }
      return
    }

    const mobilePdf = isMobilePdfMode()
    const isMobile = mobilePdf || window.innerWidth < MOBILE_BREAKPOINT_PX

    if (!isMobile) {
      if (mobileLayoutRef.current.isMobile || mobileLayoutRef.current.scale !== 1) {
        mobileLayoutRef.current = { isMobile: false, scale: 1 }
        setModileView(false)
        setMobileScale(1)
      }
      return
    }

    const viewportWidth = mobilePdf ? MOBILE_PDF_WIDTH_PX : window.innerWidth
    const horizontalPadding = mobilePdf ? 8 : 16
    const scale = Math.min(1, (viewportWidth - horizontalPadding) / SCORECARD_WIDTH_PX)
    const roundedScale = Math.round(scale * 1000) / 1000
    const prev = mobileLayoutRef.current

    if (prev.isMobile && Math.abs(prev.scale - roundedScale) < 0.001) {
      return
    }

    mobileLayoutRef.current = { isMobile: true, scale: roundedScale }
    setModileView(true)
    setMobileScale(roundedScale)
  }, [])

  useEffect(() => {
    document.documentElement.removeAttribute('data-scorecard-ready')
    if (isMobilePdfMode()) {
      document.documentElement.classList.add('scorecard-mobile-pdf')
    }
    if (isPdfDownloadPage()) {
      document.documentElement.classList.add('scorecard-pdf-download')
    }
    if (!candidate) {
      setLoading(false)
      setValidCandidate(false)
      return
    }

    setLoading(true)
    setLoadError(null)
    setSktError(false)
    setValidCandidate(true)
    setResultData(undefined)
    setSktResultDetails(undefined)
    setAllowHiddenResults(false)

    const generateDataForReport = async () => {
      try {
        const staffViewer = await isStaffViewer()
        setAllowHiddenResults(staffViewer)
        const getCandidateDetailsOfResults: any = await findCandidateReportDetails(candidate)
        if (
          getCandidateDetailsOfResults &&
          getCandidateDetailsOfResults?.candidate_report_data.length > 0 &&
          getCandidateDetailsOfResults?.candidate_report_data[0]?.is_attended &&
          (getCandidateDetailsOfResults?.candidate_report_data[0]?.is_eligible_for_result || staffViewer)
        ) {
          const candidateResultDetails = getCandidateDetailsOfResults?.candidate_report_data[0]
          //Checking skt included in the result based on condition
          let sktId =
            candidateResultDetails?.section_level_details?.test_type !== 'skt' && candidateResultDetails?.skt_id
              ? candidateResultDetails?.skt_id
              : null
          if (sktId) {
            //If Skt included checking Skt results generated?
            const getCandidateSktResults: any = await findCandidateReportDetails(sktId)
            if (getCandidateSktResults && getCandidateSktResults?.candidate_report_data?.length > 0) {
              const sktDetails = getCandidateSktResults?.candidate_report_data[0]
              const sectionLabels = sktDetails?.section_level_details?.labelsOfSection || []
              const sectionWiseNationalAverage = sectionLabels.map((sectionName: string) => {
                const nationalAvg = sktDetails.national_average_details?.sectionWiseNationalAverage?.find(
                  (natAvg: any) => natAvg.name === sectionName
                )
                return nationalAvg ? nationalAvg.sectional_national_average : null
              })
              sktDetails.section_level_details.sectionWiseNationalAverage = sectionWiseNationalAverage
              setSktResultDetails(sktDetails)
            } else {
              setSktError(true)
              return
            }
          }

          const candidate_name = candidateResultDetails?.candidate_name?.toUpperCase() || ''
          setCandidateName(candidate_name)
          if (candidateResultDetails) {
            await handleSectionPriority(candidateResultDetails)
          }
          if (candidateResultDetails?.section_level_details?.test_type === 'skt') {
            setSktResultDetails(candidateResultDetails)
          } else {
            setResultData(candidateResultDetails)
          }

          const dateOfIssue = await changeDateFormat(candidateResultDetails?.issue_date || Date.now())
          const issueDateRaw = candidateResultDetails?.issue_date ?? Date.now()
          const issueDate = new Date(issueDateRaw)
          const compareDate = new Date('2025-12-23')
          const issueDateGreater = issueDate >= compareDate
          setDigitalBadgeContentHasToChange(issueDateGreater)
          const getDateAfterOneYear = await getDateTwoYearsFromNow(
            candidateResultDetails?.section_level_details?.test_type,
            candidateResultDetails?.issue_date
          )
          const validityDate = await changeDateFormat(getDateAfterOneYear)
          const getIndexOfPCK = candidateResultDetails?.section_level_details?.labelsOfSection?.indexOf(
            'Pedagogical Content Knowledge (PCK)'
          )
          const getIndexOfGP = candidateResultDetails?.section_level_details?.labelsOfSection?.indexOf('General Pedagogy (GP)')
          if (getIndexOfPCK !== -1 || getIndexOfGP !== -1) {
            const pckPercentage =
              getIndexOfPCK !== -1 ? candidateResultDetails?.section_level_details?.sectionWisePercentage[getIndexOfPCK] : ''
            const gpPercentage =
              getIndexOfGP !== -1 ? candidateResultDetails?.section_level_details?.sectionWisePercentage[getIndexOfGP] : ''
            const learningData: any = await getLearningRecommendation(pckPercentage, gpPercentage)
            if (learningData?.length > 0) setLearningResourceList(learningData)
          }
          setTestDates({ dateOfIssue, validityDate })
        } else {
          setValidCandidate(false)
        }
      } catch (error) {
        console.error('Failed to load candidate report', error)
        setLoadError('Unable to load the scorecard. Please refresh and try again.')
        setValidCandidate(false)
      } finally {
        setLoading(false)
      }
    }
    generateDataForReport()
    return () => {
      document.documentElement.removeAttribute('data-scorecard-ready')
      document.documentElement.removeAttribute('data-scorecard-blocked')
      document.documentElement.classList.remove('scorecard-mobile-pdf')
      document.documentElement.classList.remove('scorecard-pdf-download')
    }
  }, [candidate, preview])

  useEffect(() => {
    document.documentElement.removeAttribute('data-scorecard-blocked')
    if (loading) {
      document.documentElement.removeAttribute('data-scorecard-ready')
      return
    }

    const isVisibilityBlocked =
      (resultData && !resultData?.is_visible && !allowHiddenResults) ||
      (sktResultDetails && !sktResultDetails?.is_visible && !allowHiddenResults)

    const hasReportData = Boolean(resultData || sktResultDetails)
    const scorecardReady = validCandidate && !loadError && !sktError && !isVisibilityBlocked && hasReportData

    if (scorecardReady) {
      const frameId = requestAnimationFrame(() => {
        document.documentElement.setAttribute('data-scorecard-ready', 'true')
      })
      return () => cancelAnimationFrame(frameId)
    }

    document.documentElement.removeAttribute('data-scorecard-ready')
    // Lets server-side PDF capture stop waiting and report why, instead of timing out.
    if (isVisibilityBlocked) {
      document.documentElement.setAttribute('data-scorecard-blocked', 'hidden')
    } else if (!validCandidate) {
      document.documentElement.setAttribute('data-scorecard-blocked', 'unavailable')
    }
  }, [loading, validCandidate, loadError, sktError, resultData, sktResultDetails, allowHiddenResults])

  useEffect(() => {
    if (loading || !validCandidate) {
      return
    }

    updateMobileLayout()

    let resizeTimer: ReturnType<typeof setTimeout>
    const onResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(updateMobileLayout, 200)
    }
    window.addEventListener('resize', onResize)

    const layoutTimer = setTimeout(updateMobileLayout, 600)

    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(resizeTimer)
      clearTimeout(layoutTimer)
    }
  }, [loading, validCandidate, resultData, sktResultDetails, updateMobileLayout])

  //-------------------------------------------------
  return (
    <div
      className={`scorecard-outer${isPdfDownloadPage() ? ' scorecard-pdf-download' : ''}${
        isMobilePdfMode() ? ' scorecard-mobile-pdf' : ''
      }`}
    >
      {loading ? (
        <div className="scorecard-pending">
          <p style={{ fontSize: '18px', color: '#0c5692', textAlign: 'center' }}>
            Please wait, we are verifying your profile...
          </p>
        </div>
      ) : loadError ? (
        <div className="scorecard-pending">
          <p style={{ fontSize: '18px', color: '#c44e3f', textAlign: 'center' }}>{loadError}</p>
        </div>
      ) : sktError ? (
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
              alignContent: 'center',
              margin: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img style={{ marginRight: '2rem' }} src={NoData} alt="invalid candidate" width="40%" height="40%" />
            </div>
            <div>
              <Button onClick={() => window.history.back()}>
                The SKT result for this candidate is missing. Please generate the result to proceed
              </Button>
            </div>
          </div>
        </div>
      ) : !validCandidate ? (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img style={{ marginRight: '2rem' }} src={Notfound} alt="invalid candidate" width="50%" height="50%" />
          </div>
        </div>
      ) : (resultData && !resultData?.is_visible && !allowHiddenResults) || (sktResultDetails && !sktResultDetails?.is_visible && !allowHiddenResults) ? (
        <div className="scorecard-pending">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <img style={{ marginRight: '2rem' }} src={ProtectedFile} alt="invalid candidate" width="50%" height="50%" />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              <div
                style={{
                  backgroundColor: '#0c5692',
                  padding: '30px 40px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  maxWidth: '600px',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                <p style={{ fontSize: '20px', fontWeight: '600', color: 'white' }}> Hi {candidateName},</p>
                <p style={{ fontSize: '16px', margin: '10px 0', color: 'white' }}> Your results are ready and currently under review.<br />We’ll notify you as soon as they are approved and published.<br />If you have any questions feel free to reach out to the <strong>CENTA team</strong> — we're here to help! 🤝</p>
                <a
                  href="mailto:team@centa.org"
                  style={{
                    display: 'inline-block',
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#ffffff',
                    color: '#0c5692',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  Connect with Us 💬
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="scorecard-viewport">
          <div className="scorecard-scale-shell">
            <div
              className="scorecard-document"
              ref={componentPdf}
              style={
                modileView && !isPdfDownloadPage()
                  ? ({
                      transform: `scale(${mobileScale})`,
                      transformOrigin: 'top center',
                    } as React.CSSProperties)
                  : undefined
              }
            >
          {resultData && resultData !== undefined && (
            <>
              {/* ------------------------------------------1st page starts----------------------------------------- */}
              <div className="scorecard-page scorecard-page-tq">
                {resultData?.section_level_details?.test_type === 'jharkhand' && (
                  <img src={patternCoverPage} alt="pattern" style={{ position: 'absolute', width: '100%', opacity: 0.1, top: '10rem' }} />
                )}
                {resultData?.section_level_details?.test_type === 'jharkhand' && (
                  <img
                    src={patternCoverPage}
                    alt="pattern"
                    style={{
                      position: 'absolute',
                      width: '90%',
                      opacity: 0.1,
                      top: '40rem',
                      transform: 'rotate(-15deg)',
                      right: '28%',
                    }}
                  />
                )}
                <div style={{ width: '100%' }}>
                  <img src={letterheadTop} alt="head img" width="100%" />
                </div>
                  {preview &&
                    <div style={{ position: 'absolute', top: '12rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}

                <div style={{ position: 'absolute', top: '16rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {resultData?.section_level_details?.test_type === 'jharkhand' ? (
                      <img src={centaSktLogo} alt="centa_log" style={{ width: '350px', height: '160px' }} />
                    ) : (
                      <img src={centaTqLogo} alt="centa_log" style={{ width: '220px', height: '220px' }} />
                    )}
                  </div>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    top: resultData?.section_level_details?.test_type === 'jharkhand' ? '27rem' : '30rem',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {resultData?.section_level_details?.test_type === 'jharkhand' ? (
                      <div className="nunito-text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <h1 style={{ fontSize: '46px', fontWeight: 800, color: '#6e6e6e', marginBottom: '5px' }}>
                          {/* CENTA<span style={{ position: 'absolute', bottom: '7.5rem', left: '15rem', fontSize: '28px' }}>&reg;</span>
                          &nbsp;&nbsp; */}
                          STATEWIDE TEACHER
                        </h1>
                        <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#6e6e6e', marginTop: '4px' }}>COMPETENCY TEST</h1>
                      </div>
                    ) : (
                      <h1 className="nunito-text" style={{ fontSize: '40px', fontWeight: 'bold', color: '#6e6e6e' }}>
                        CENTA<span style={{ position: 'absolute', bottom: '3rem', left: '14.5rem', fontSize: '28px' }}>&reg;</span>
                        &nbsp;&nbsp;TEACHING QUOTIENT
                      </h1>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    top: resultData?.section_level_details?.test_type === 'jharkhand' ? '36rem' : '35rem',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '20px', color: 'black' }}>
                      This is to confirm that
                    </h3>
                  </div>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    top: resultData?.section_level_details?.test_type === 'jharkhand' ? '38rem' : '37rem',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '28px', fontWeight: 'bold', color: 'black' }}>
                      {candidateName}
                    </h3>
                  </div>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    top: resultData?.section_level_details?.test_type === 'jharkhand' ? '41rem' : '40rem',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '22px', color: 'black' }}>
                      participated in
                    </h3>
                  </div>
                </div>
                <div style={{ position: 'absolute', top: '43rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {resultData?.section_level_details?.test_type === 'jharkhand' ? (
                      <div className="nunito-text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <h3 style={{ fontSize: '24px', color: 'black', fontWeight: 'bold' }}>The statewide teacher competency test</h3>
                        <h3 className="nunito-text" style={{ fontSize: '24px', color: 'black', fontWeight: 'bold' }}>
                          conducted by CENTA
                          <span style={{ position: 'absolute', bottom: '1.7rem', left: '32rem', fontSize: '20px', fontWeight: 'bold' }}>&reg;</span>
                        </h3>
                      </div>
                    ) : (
                      // <h3 className="nunito-text" style={{ fontSize: '24px', color: 'black', fontWeight: 'bold' }}>
                      //   The statewide teacher competency test conducted by CENTA
                      //   <span style={{ position: 'absolute', bottom: '1.7rem', left: '16.5rem', fontSize: '20px', fontWeight: 'bold' }}>&reg;</span>
                      //   &nbsp;&nbsp;
                      // </h3>
                      <h3 className="nunito-text" style={{ fontSize: '24px', color: 'black', fontWeight: 'bold' }}>
                        CENTA
                        <span style={{ position: 'absolute', bottom: '1.7rem', left: '16.5rem', fontSize: '20px', fontWeight: 'bold' }}>&reg;</span>
                        &nbsp;&nbsp; TEACHING QUOTIENT TEST
                      </h3>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    top: resultData?.section_level_details?.test_type === 'jharkhand' ? '49rem' : '46rem',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                      TEST TRACK : <span style={{ fontWeight: 'bold' }}>{resultData?.section_level_details?.test_track_name}</span>
                    </h3>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                      DATE OF TEST : <span style={{ fontWeight: 'bold' }}>{resultData?.section_level_details?.test_date}</span>
                    </h3>
                  </div>
                </div>

                {/* <div style={{ width: '100%', position: 'absolute', top: '30rem' }}> */}
                <div style={{ width: '100%', position: 'absolute', top: '20.6rem' }}>
                  <img src={letterEnd} alt="head img" width="100%" />
                </div>
              </div>
              {/* ------------------------------------------1st page ends----------------------------------------- */}

              {/* ------------------------------------------2nd page starts--------------------------------------- */}

              <div className="scorecard-page scorecard-page-tq">
                <div style={{ padding: '1rem' }}>
                  <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                </div>

                <div>
                  {/* test details */}
                  <div>
                    <div className="scorecard-test-details-bar scorecard-panel">
                      <div className="scorecard-test-details-row">
                        <div style={{ backgroundColor: 'white', borderRadius: '10px', marginRight: '20px', marginLeft: '1rem' }}>
                          <img
                            src={resultData?.section_level_details?.test_type === 'jharkhand' ? centaCIcon : centaTqLogo}
                            alt="centa_log"
                            style={{ width: '60px', height: '60px' }}
                          />
                        </div>

                        <div style={{ display: 'flex', marginRight: '2rem' }}>
                          <div className="nunito-text" style={{ marginTop: '5px' }}>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              TEST TRACK &nbsp;:
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}>
                                {' '}
                                {resultData?.section_level_details?.test_track_name}
                              </span>
                            </p>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              ISSUE DATE &nbsp; :{' '}
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}>{testDates?.dateOfIssue}</span>{' '}
                            </p>
                          </div>
                        </div>

                        <div>
                          <div className="nunito-text" style={{ marginTop: '5px' }}>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              TEST DATE :
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}>
                                {' '}
                                {resultData?.section_level_details?.test_date}
                              </span>
                            </p>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              VALIDITY &nbsp;&nbsp;&nbsp;:
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}> {testDates?.validityDate}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ----- * test details ends *----- */}

                  {/* ------------- */}

                  {/*details div starts  */}

                  <div>
                    <div
                      className="scorecard-panel"
                      style={{
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        marginTop: '0.5rem',
                        paddingTop: '1rem',
                        background: 'linear-gradient(83deg, rgba(221,194,116,1) 16%, rgba(232,212,153,1) 54%, rgba(225,198,120,1) 69%)',
                        borderRadius: '5px',
                      }}
                    >
                      <div className="scorecard-score-summary">
                        <div style={{ paddingBottom: '1rem' }}>
                          <div style={{ display: 'flex', paddingTop: '1.4rem' }}>
                            <img src={scoreIconImg} style={{ height: '38px', width: '38px' }} />
                            <h3 style={{ fontSize: '19px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                              <span style={{ marginTop: '7px', marginLeft: '4px' }}>{formatScorecardDecimal(resultData?.section_level_details?.total_marks)}</span>
                            </h3>
                          </div>
                          <p style={{ fontSize: '12px' }}>scored out of {formatScorecardDecimal(resultData?.section_level_details?.total_paper_score)}</p>
                        </div>

                        <div>
                          <div style={{ display: 'flex', paddingTop: '1.2rem' }}>
                            <img src={graphIconImg} style={{ height: '40px', width: '40px', marginLeft: '10px' }} alt="graphicon" />
                            <h3 style={{ fontSize: '18px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                              <span style={{ marginTop: '10px' }}>
                                {formatScorecardDecimal(candidateTotalResultPercentage)}{' '}
                                %
                              </span>
                            </h3>
                          </div>
                          <p style={{ fontSize: '12px' }}>
                            against an average of{' '}
                            {formatScorecardDecimal(resultData?.national_average_details?.total_national_average)}{' '}
                            %
                          </p>
                        </div>

                        {/* <div>
                        <div style={{ display: 'flex', paddingTop: '1.2rem' }}>
                          <img src={qualifiedIconImg} alt="" style={{ width: '42px', height: '40px' }} />

                          <h3 style={{ fontSize: '18px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                            {resultData?.section_level_details?.All_Items_Greater_Than30 && candidateTotalResultPercentage >= 50 ? (
                              <span style={{ marginTop: '10px' }}>Qualified</span>
                            ) : (
                              <span style={{ marginTop: '10px', fontSize: '16px' }}>Not Eligible</span>
                            )}
                          </h3>
                        </div>
                        <p style={{ fontSize: '12px' }}>
                          To Take part in CENTA International
                          <br />
                          TPO Finals
                        </p>
                      </div> */}

                        {resultData?.section_level_details?.test_type !== 'jharkhand' && (
                          <div>
                            <div style={{ display: 'flex', paddingTop: '1.4rem' }}>
                              {resultData?.section_level_details?.All_Items_Greater_Than30 && candidateTotalResultPercentage >= 50 ? (
                                <img src={eligibleIcon} style={{ height: '32px', width: '32px' }} alt="EligibleIcon" />
                              ) : (
                                <img src={noteligibleIcon} style={{ height: '35px', width: '35px' }} alt="notEligibleicon" />
                              )}
                              <h3 style={{ fontSize: '18px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                                {resultData?.section_level_details?.All_Items_Greater_Than30 && candidateTotalResultPercentage >= 50 ? (
                                  <span style={{ marginTop: '7px' }}>Eligible</span>
                                ) : (
                                  <span style={{ marginTop: '5px', marginLeft: '4px', fontSize: '16px', marginBottom: '5px' }}>Not Eligible</span>
                                )}
                              </h3>
                            </div>
                            <p style={{ fontSize: '12px' }}>for digital badge</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {preview &&
                    <div style={{ position: 'absolute', top: '23rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                  {/* ----------------------------------------- */}

                  {/* -----------------section wise details chart------------------- */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        marginTop: '1rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.5rem',
                        background:
                          'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                        width: '95%',
                      }}
                    >
                      <h2 style={{ textAlign: 'center', marginTop: '10px', color: '#6e6e6e', marginBottom: '10px' }}>
                        Your section level performance
                      </h2>

                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned</p>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '10px' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                          <p style={{ color: '#96c63c', margin: '0' }}>&nbsp;Total Score Obtained</p>
                        </div>
                      </div>

                      {resultData?.section_level_details && (
                        <div style={{ padding: '1rem', height: 'auto', display: 'flex', justifyContent: 'center' }}>
                          <SectionWiseChart
                            labelsOfSection={resultData?.section_level_details?.labelsOfSection}
                            sectionWiseTotal={resultData?.section_level_details?.sectionWiseTotal}
                            sectionWiseTotalObtained={resultData?.section_level_details?.sectionWiseTotalObtained}
                            bottomValue={sectionBottomValue}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {/* ------------------------------------------------- */}
                </div>
                <div className="scorecard-page-footer">
                  <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                </div>
              </div>
              {/* </div> */}

              {/* ------------------------------------------2nd page ends--------------------------------------- */}

              {/* -----------------------------------------3nd page starts--------------------------------------*/}

              <div className="scorecard-page scorecard-page-tq">
                <div style={{ padding: '1rem' }}>
                  <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                </div>
                <div className="nunito-text">
                  <div className="scorecard-flag-message">{renderOptionFlagMessage(resultData?.option_flag_details)}</div>
                </div>
                {/* ------------------------------report theme chart starts-------------------------------- */}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                      borderRadius: 5,
                      width: '95%',
                      height: 'auto',
                      paddingTop: '1rem',
                      marginTop: '10px',
                      // background:"#f0fafc"
                      background:
                        'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                    }}
                  >
                    {preview &&
                      <div style={{ position: 'absolute', top: '8rem', left: '35rem' }}>
                        <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                      </div>}
                    <h2 style={{ textAlign: 'center', color: '#6e6e6e', marginBottom: '10px', marginTop: '10px' }}>
                      Section level - Report themes graphs
                    </h2>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                        <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned</p>
                      </div>
                      <div style={{ display: 'flex', marginLeft: '10px' }}>
                        <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                        <p style={{ marginLeft: '5px', color: '#96c63c', margin: '0' }}>&nbsp;Total Score Obtained</p>
                      </div>
                    </div>

                    {resultData?.section_wise_report_theme_details && (
                      <Row gutter={16} style={{ width: '100%' }}>
                        {resultData?.section_wise_report_theme_details.map((item: any, index: number) => (
                          <Col span={12} key={index} style={{ width: '80%' }}>
                            <h4 style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '1rem' }}>{item.section_name}</h4>

                            <div
                              style={{
                                flex: '1',
                                margin: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <div>
                                <SectionTagChart
                                  chartData={item.report_theme_details}
                                  style={{ width: '90%', height: 'auto' }}
                                  totalItems={resultData?.section_wise_report_theme_details.length}
                                />
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                </div>
                {/* ------------------------------report theme chart ends-------------------------------- */}

                {/* -------------------------------footer image------------------------------------------ */}
                <div className="scorecard-page-footer">
                  <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                </div>
                {/* -------------------------------footer image------------------------------------------ */}
              </div>

              {/* -----------------------------------------3nd page ends--------------------------------------- */}

              {/* -----------------------------------------4th page starts------------------------------------- */}

              <div className="scorecard-page scorecard-page-tq">
                {/* --------------Header image-------------- */}
                <div style={{ padding: '1rem' }}>
                  <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                </div>
                {/* ----------------Blooms level Chart------- */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                      borderRadius: 5,
                      marginTop: '10px',
                      paddingTop: '1rem',
                      width: '95%',
                      background:
                        'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                    }}
                  >
                    <div
                      style={{
                        paddingTop: '1rem',
                        height: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <h2 style={{ textAlign: 'center', marginTop: '2rem', color: '#6e6e6e', marginBottom: '1rem' }}>
                        Performance based on Bloom's taxonomy
                      </h2>
                      {preview &&
                        <div style={{ position: 'absolute', top: '8rem', left: '35rem' }}>
                          <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                        </div>}
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned</p>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '10px' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#96c63c', margin: '0' }}>&nbsp;Total Score Obtained</p>
                        </div>
                      </div>

                      {resultData?.blooms_level_details && (
                        <SectionWiseChart
                          labelsOfSection={resultData?.blooms_level_details?.labelsOfSection}
                          sectionWiseTotal={resultData?.blooms_level_details?.sectionWiseTotal}
                          sectionWiseTotalObtained={resultData?.blooms_level_details?.sectionWiseTotalObtained}
                          bottomValue={tagBottomValue}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* ----------------------Blooms level chart ends------------------ */}

                {/* ----------------------national average base chart-------------- */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                      height: 'auto',
                      marginTop: '10px',
                      paddingTop: '1rem',
                      width: '95%',
                      background:
                        'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                    }}
                  >
                    <div
                      style={{
                        padding: '1rem',
                        height: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <h2 style={{ textAlign: 'center', marginTop: '2rem', color: '#6e6e6e' }}>Your National Average Based Sectional Performance</h2>

                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#0c5692' }}>Percentage of National Average </p>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '10px' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#96c63c', margin: '0' }}>&nbsp;Percentage of Candidate</p>
                        </div>
                      </div>
                      {resultData?.section_level_details && resultData?.national_average_details ? (
                        <SectionWiseChart
                          labelsOfSection={resultData?.section_level_details?.labelsOfSection}
                          sectionWiseTotal={resultData?.section_level_details?.sectionWiseNationalAverage}
                          sectionWiseTotalObtained={resultData?.section_level_details?.sectionWisePercentage}
                          bottomValue={tagBottomValue}
                        />
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </div>
                {/* ----------------------national average base chart ends--------- */}
                {/* -------------------------------footer image------------------------------------------ */}
                <div className="scorecard-page-footer">
                  <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                </div>
                {/* -------------------------------footer image------------------------------------------ */}
              </div>

              {/* -----------------------------------------4th page ends--------------------------------------- */}
              {/* -----------------------------------------5th page starts- Learning Recommendation------------------------------------ */}

              {learningResourcesList.length > 0 && (
                <div
                  className="nunito-text scorecard-page scorecard-page-tq"
                >
                  <div style={{ padding: '1rem' }}>
                    <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                  </div>
                  {preview &&
                    <div style={{ position: 'absolute', top: '8rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '2rem',
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        background:
                          'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                        height: '100%',
                        marginBottom: '1rem',
                        width: '95%',
                      }}
                    >
                      <div>
                        <h1 style={{ fontSize: '35px', fontWeight: 'bold', textAlign: 'center', marginTop: '2rem', color: '#6e6e6e' }}>
                          Learning Recommendation
                        </h1>
                        <h3 style={{ color: '#4a4949', margin: '1rem 0 1rem 0' }}>
                          CENTA recommends courses tailored to your strengths and areas for growth based on your performance. These courses can
                          enhance your skills further and address areas needing improvement. Consult CENTA advisors to create a personalized plan
                          aligned with your goals, ensuring you make well-informed choices for your development.
                        </h3>
                      </div>
                      {/* learing resourses */}
                      <div>
                        <Row gutter={16} style={{ width: '100%' }}>
                          {learningResourcesList.map((resource: any, resourceIndex: number) => (
                            <Col span={12} key={resourceIndex} style={{ marginBottom: '1rem' }}>
                              {/* Resource item with purpose and links */}
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%',
                                  padding: '10px',
                                  background: 'linear-gradient(209deg, rgba(242,242,242,1) 1%, rgba(255,255,255,1) 100%)',
                                  borderRadius: '5px',
                                  border: '1px solid rgba(128, 128, 128, 0.5)',
                                }}
                              >
                                <h4 style={{ fontWeight: 'Bold' }}>{resource.resourceType}</h4>
                                <p style={{ flex: '1', fontSize: '14px' }}>{resource.purpose}</p>

                                {/* Display each link */}
                                <div style={{ display: 'flex' }}>
                                  {resource.links.map((link: string, index: number) => (
                                    <div key={index} style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>
                                        <img src={linkPng} width="25px" height="25px" alt="link-icon" />
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>

                      {/* ---- */}
                    </div>
                  </div>

                  <div className="scorecard-page-footer">
                    <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                  </div>
                </div>
              )}

              {/* -----------------------------------------5th page ends- Learning Recommendation-------------------------------------- */}

              {/* -------------6th page starts-(digital badge)------------------------------------------------- */}
              {resultData?.section_level_details?.test_type !== 'jharkhand' && (
                <div
                  className="nunito-text scorecard-page scorecard-page-tq"
                >
                  <div style={{ padding: '1rem' }}>
                    <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                  </div>
                  {preview &&
                    <div style={{ position: 'absolute', top: '12rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        display: 'flex',

                        padding: '2rem',
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        marginTop: '2rem',
                        background:
                          'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                        height: '200mm',
                        marginBottom: '2rem',
                        width: '95%',
                        position: 'relative',
                      }}
                    >
                      <div>
                        <h1 style={{ fontSize: '35px', fontWeight: 'bold', textAlign: 'center', marginTop: '2rem', color: '#6e6e6e' }}>
                          DIGITAL BADGE
                        </h1>

                        <div style={{ margin: '2rem 0 2rem 1rem' }}>
                          <h3 style={{ fontSize: '15px', color: 'black' }}>
                            Candidate Name : <span style={{ fontSize: '16px', fontWeight: 'bold' }}> {candidateName}</span>
                          </h3>
                          <h3 style={{ fontSize: '15px', color: 'black' }}>
                            Test Track &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:{' '}
                            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{resultData?.section_level_details?.test_track_name}</span>
                          </h3>
                          <h3 style={{ fontSize: '15px', color: 'black' }}>
                            Date Of Test &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; :{' '}
                            <span style={{ fontSize: '16px', fontWeight: 'bold' }}> {resultData?.section_level_details?.test_date}</span>
                          </h3>
                        </div>
                        <div style={{ marginLeft: '1rem', zIndex: 10 }}>
                          <h3>Dear {candidateName},</h3>
                          <div>
                            <img
                              src={centaTqLogo}
                              alt="centa_logo"
                              style={{
                                position: 'absolute',
                                bottom: '0',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '125px',
                                height: '125px',
                                opacity: 0.2,
                                zIndex: 1,
                                top: '20rem',
                              }}
                            />
                            {resultData?.section_level_details?.All_Items_Greater_Than30 && candidateTotalResultPercentage >= 50 ? (
                              <div style={{ display: 'flex', justifyContent: 'center', width: '100' }}>
                                {!digitalBadgeContentHasToChange ? (<h3 style={{ position: 'relative', zIndex: 10 }}>
                                  {' '}
                                  Congratulations on securing the CENTA Teaching Quotient digital badge and qualifying for the CENTA TPO finals
                                  (Details are in your report). We will connect with you in the coming days on the next steps for you to access the
                                  badge. We hope you found this experience fruitful.
                                  <br />
                                  Please respond back to{' '}
                                  <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                    <a href="mailto:team@centa.org" style={{ color: 'inherit', textDecoration: 'none', fontStyle: 'italic' }}>
                                      team@centa.org
                                    </a>
                                  </span>{' '}
                                  in case of any queries.
                                </h3>) : (
                                  <h3 style={{ position: 'relative', zIndex: 10 }}>
                                    {' '}
                                    Congratulations on securing the CENTA Teaching Quotient digital badge (details are available in your report). We will connect with you in the coming days on the next steps for you to access the badge. We hope you found this experience fruitful Kindly confirm your approval so that we may proceed further.
                                    <br />
                                    Please respond back to{' '}
                                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                      <a href="mailto:team@centa.org" style={{ color: 'inherit', textDecoration: 'none', fontStyle: 'italic' }}>
                                        team@centa.org
                                      </a>
                                    </span>{' '}
                                    in case of any queries.
                                  </h3>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                {!digitalBadgeContentHasToChange ? (<h3 style={{ position: 'relative', zIndex: 10 }}>
                                  Please note that the eligibility criteria for CENTA TQ Badge and qualifying for CENTA TPO Finals are: 50% or more
                                  overall and 30% or more in each sub-section (MCQ). Since you did not meet these criteria you are, unfortunately, not
                                  eligible for the badge. You can take the test in the same subject track after a gap of 3 months. We hope you found
                                  this experience fruitful.
                                  <br />
                                  Please respond back to{' '}
                                  <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                    <a href="mailto:team@centa.org" style={{ color: 'inherit', textDecoration: 'none', fontStyle: 'italic' }}>
                                      team@centa.org
                                    </a>
                                  </span>{' '}
                                  in case of any queries.
                                </h3>) : (
                                  <h3 style={{ position: 'relative', zIndex: 10 }}>
                                    Please note that the eligibility criteria for the CENTA TQ Badge are 50% or more overall and 30% or more in each sub-section (MCQ). Since you did not meet these criteria, you are unfortunately not eligible for the badge. You can take the test in the same subject track after a gap of 3 months. We hope you found this experience fruitful.
                                    <br />
                                    Please respond back to{' '}
                                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                      <a href="mailto:team@centa.org" style={{ color: 'inherit', textDecoration: 'none', fontStyle: 'italic' }}>
                                        team@centa.org
                                      </a>
                                    </span>{' '}
                                    in case of any queries.
                                  </h3>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ margin: '3rem 0 0 1rem' }}>
                          <h3 style={{ opacity: '0.5', fontStyle: 'italic', fontSize: '14px' }}>The eligibility criteria to earn a digital badge</h3>
                          <div style={{ marginTop: '1rem', fontStyle: 'italic', fontSize: '12px' }}>
                            {' '}
                            <h4 style={{ opacity: '0.5' }}>
                              (a) <span> 50% or more overall</span>
                            </h4>
                            <h4 style={{ opacity: '0.5' }}>
                              (b) <span> 30% or more in each section</span>
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="scorecard-page-footer">
                    <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                  </div>
                </div>
              )}
              {/* ------------- 6th page ends (digital badge)-------------------------------------------- */}
            </>
          )}
          {/*-------------------------SKT Report---------------------------------------------------------------------- */}
          {sktResultDetails && sktResultDetails !== undefined && (
            <>
              {/* SKT 1st page */}
              <div className="scorecard-page scorecard-page-skt">
                <div style={{ width: '100%' }}>
                  <img src={letterheadTop} alt="head img" width="100%" />
                </div>
                
                <div style={{ position: 'absolute', top: '16rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <img src={centaSktLogo} alt="centa_log" style={{ width: '350px', height: '170px' }} />
                  </div>
                </div>
                {preview &&
                    <div style={{ position: 'absolute', top: '12rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                <div style={{ position: 'absolute', top: '28rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h1 className="nunito-text" style={{ fontSize: '38px', fontWeight: 'bold', color: '#6e6e6e' }}>
                      CENTA<span style={{ position: 'absolute', bottom: '3rem', right: '37.3rem', fontSize: '26px' }}>&reg;</span>
                      &nbsp;&nbsp;SUBJECT KNOWLEDGE TEST
                    </h1>
                  </div>
                </div>

                <div style={{ position: 'absolute', top: '32rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '20px', color: 'black' }}>
                      This is to confirm that
                    </h3>
                  </div>
                </div>

                <div style={{ position: 'absolute', top: '35rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '28px', fontWeight: 'bold', color: 'black' }}>
                      {candidateName}
                    </h3>
                  </div>
                </div>

                <div style={{ position: 'absolute', top: '38rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '22px', color: 'black' }}>
                      has participated in
                    </h3>
                  </div>
                </div>
                <div style={{ position: 'absolute', top: '42rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '24px', color: 'black', fontWeight: 'bold' }}>
                      CENTA
                      <span style={{ position: 'absolute', bottom: '1.7rem', left: '16rem', fontSize: '20px', fontWeight: 'bold' }}>&reg;</span>
                      &nbsp;&nbsp; SUBJECT KNOWLEDGE TEST
                    </h3>
                  </div>
                </div>

                <div style={{ position: 'absolute', top: '45rem', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                      TEST TRACK : <span style={{ fontWeight: 'bold' }}>{sktResultDetails?.section_level_details?.test_track_name}</span>
                    </h3>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h3 className="nunito-text" style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                      DATE OF TEST : <span style={{ fontWeight: 'bold' }}>{sktResultDetails?.section_level_details?.test_date}</span>
                    </h3>
                  </div>
                </div>
                <div style={{ width: '100%', position: 'absolute', top: '20.6rem' }}>
                  <img src={letterEnd} alt="head img" width="100%" />
                </div>
              </div>

              <div className="scorecard-page scorecard-page-skt">
                <div style={{ padding: '1rem' }}>
                  <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                </div>

                <div>
                  {/* test details */}
                  <div>
                    <div className="scorecard-test-details-bar scorecard-panel" style={{ justifyContent: 'center' }}>
                      <div className="scorecard-test-details-row">
                        <div style={{ backgroundColor: 'white', borderRadius: '50px', marginRight: '20px', marginLeft: '1rem', height: '62px' }}>
                          <img src={centaCIcon} alt="centa_log" style={{ width: '60px', height: '60px' }} />
                        </div>

                        <div style={{ display: 'flex', marginRight: '5px' }}>
                          <div className="nunito-text" style={{ marginTop: '5px' }}>
                            <p
                              style={{
                                margin: 0,
                                color: '#ffff',
                                fontWeight: 'bold',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                display: 'flex',
                                flexWrap: 'wrap',
                              }}
                            >
                              TEST TRACK &nbsp;:
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px', flex: 1, minWidth: 0 }}>
                                {' '}
                                &nbsp;{sktResultDetails?.section_level_details?.test_track_name}
                              </span>
                            </p>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              ISSUE DATE &nbsp; :{' '}
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}>{testDates?.dateOfIssue}</span>{' '}
                            </p>
                          </div>
                        </div>
                        {preview &&
                    <div style={{ position: 'absolute', top: '12rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                        <div>
                          <div className="nunito-text" style={{ marginTop: '5px' }}>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              TEST DATE :
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}>
                                {' '}
                                {sktResultDetails?.section_level_details?.test_date}
                              </span>
                            </p>
                            <p style={{ margin: 0, color: '#ffff', fontWeight: 'bold' }}>
                              VALIDITY &nbsp;&nbsp;&nbsp;:
                              <span style={{ color: '#ffff', fontWeight: 'normal', fontSize: '15px' }}> {testDates?.validityDate}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div
                      className="scorecard-panel"
                      style={{
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        marginTop: '0.5rem',
                        paddingTop: '1rem',
                        background: 'linear-gradient(83deg, rgba(221,194,116,1) 16%, rgba(232,212,153,1) 54%, rgba(225,198,120,1) 69%)',
                        borderRadius: '5px',
                      }}
                    >
                      <div className="scorecard-score-summary">
                        <div style={{ paddingBottom: '1rem' }}>
                          <div style={{ display: 'flex', paddingTop: '1.4rem' }}>
                            <img src={scoreIconImg} style={{ height: '38px', width: '38px' }} />
                            <h3 style={{ fontSize: '19px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                              <span style={{ marginTop: '7px', marginLeft: '4px' }}>{formatScorecardDecimal(sktResultDetails?.section_level_details?.total_marks)}</span>
                            </h3>
                          </div>
                          <p style={{ fontSize: '12px' }}>scored out of {formatScorecardDecimal(sktResultDetails?.section_level_details?.total_paper_score)}</p>
                        </div>

                        <div>
                          <div style={{ display: 'flex', paddingTop: '1.2rem' }}>
                            <img src={graphIconImg} style={{ height: '40px', width: '40px', marginLeft: '10px' }} alt="graphicon" />
                            <h3 style={{ fontSize: '18px', display: 'flex', flexDirection: 'column', margin: '0' }}>
                              <span style={{ marginTop: '10px' }}>
                                {formatScorecardDecimal(sktTotalResultPercentage)}{' '}
                                %
                              </span>
                            </h3>
                          </div>
                          <p style={{ fontSize: '12px' }}>
                            against an average of{' '}
                            {formatScorecardDecimal(sktResultDetails?.national_average_details?.total_national_average)}{' '}
                            %
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ----------------------------------------- */}

                  {/* -----------------section wise details chart------------------- */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        marginTop: '1rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.5rem',
                        background:
                          'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                        width: '95%',
                      }}
                    >
                      {sktResultDetails?.section_level_details && (
                        <div className="scorecard-skt-charts-row" style={{ padding: '0.75rem 0.5rem' }}>
                          <div className="scorecard-skt-chart-column">
                            <h3 style={{ textAlign: 'center', marginTop: '6px', color: '#6e6e6e', marginBottom: '0.75rem', fontSize: '15px' }}>
                              Overall performance
                            </h3>
                            <SectionWiseChart
                              labelsOfSection={['Overall']}
                              sectionWiseTotal={sktResultDetails?.section_level_details?.sectionWiseTotal}
                              sectionWiseTotalObtained={sktResultDetails?.section_level_details?.sectionWiseTotalObtained}
                              bottomValue={sectionBottomValue}
                            />
                            <div className="scorecard-chart-legend">
                              <div className="scorecard-chart-legend-item">
                                <div
                                  style={{ width: '10px', height: '10px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px', flexShrink: 0 }}
                                ></div>
                                <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned</p>
                              </div>
                              <div className="scorecard-chart-legend-item">
                                <div
                                  style={{ width: '10px', height: '10px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px', flexShrink: 0 }}
                                ></div>
                                <p style={{ color: '#96c63c', marginLeft: '5px' }}>Total Score Obtained</p>
                              </div>
                            </div>
                          </div>

                          {sktResultDetails?.national_average_details && (
                            <div className="scorecard-skt-chart-column">
                              <h3 style={{ textAlign: 'center', marginTop: '6px', color: '#6e6e6e', marginBottom: '0.75rem', fontSize: '15px' }}>
                                Comparison with national average
                              </h3>

                              <SectionWiseChart
                                labelsOfSection={['Overall']}
                                sectionWiseTotal={sktResultDetails?.section_level_details?.sectionWiseNationalAverage}
                                sectionWiseTotalObtained={[sktTotalResultPercentage]}
                                bottomValue={sectionBottomValue}
                              />
                              <div className="scorecard-chart-legend">
                                <div className="scorecard-chart-legend-item">
                                  <div
                                    style={{ width: '10px', height: '10px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px', flexShrink: 0 }}
                                  ></div>
                                  <p style={{ marginLeft: '5px', color: '#0c5692' }}>National Average</p>
                                </div>
                                <div className="scorecard-chart-legend-item">
                                  <div
                                    style={{ width: '10px', height: '10px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px', flexShrink: 0 }}
                                  ></div>
                                  <p style={{ color: '#96c63c', marginLeft: '5px' }}>Percentage of Candidate</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* ------------------------------------------------- */}
                </div>
                <div className="scorecard-page-footer">
                  <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                </div>
              </div>

              <div className="scorecard-page scorecard-page-skt">
                {/* --------------Header image-------------- */}
                <div style={{ padding: '1rem' }}>
                  <img src={pageHeader} alt="page header image" style={{ width: '100%' }} />
                </div>
                {preview &&
                    <div style={{ position: 'absolute', top: '12rem', left: '35rem' }}>
                      <img src={previewLogo} alt="preview_logo" style={{ width: '220px', height: '220px', opacity: 0.4 }} />
                    </div>}
                <div className="nunito-text">
                  <div className="scorecard-flag-message">{renderOptionFlagMessage(sktResultDetails?.option_flag_details)}</div>
                </div>
                {/* ----------------Blooms level Chart------- */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                      borderRadius: 5,
                      marginTop: '0.5rem',
                      paddingTop: '0.75rem',
                      width: '95%',
                      background:
                        'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                    }}
                  >
                    <div
                      style={{
                        paddingTop: '0.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <h2 style={{ textAlign: 'center', marginTop: '0.75rem', color: '#6e6e6e', marginBottom: '0.75rem' }}>
                        Performance based on Bloom's taxonomy
                      </h2>

                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned</p>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '10px' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#96c63c', margin: '0' }}>&nbsp;Total Score Obtained</p>
                        </div>
                      </div>

                      {sktResultDetails?.blooms_level_details && (
                        <SectionWiseChart
                          labelsOfSection={sktResultDetails?.blooms_level_details?.labelsOfSection}
                          sectionWiseTotal={sktResultDetails?.blooms_level_details?.sectionWiseTotal}
                          sectionWiseTotalObtained={sktResultDetails?.blooms_level_details?.sectionWiseTotalObtained}
                          bottomValue={tagBottomValue}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* ----------------------Blooms level chart ends------------------ */}

                {/* ----------------------national average base chart-------------- */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                      height: 'auto',
                      marginTop: '0.5rem',
                      paddingTop: '0.75rem',
                      width: '95%',
                      background:
                        'linear-gradient(90deg, rgba(246,246,246,1) 6%, rgba(255,255,255,1) 27%, rgba(255,255,255,1) 68%, rgba(246,246,246,1) 100%);',
                    }}
                  >
                    <div
                      style={{
                        padding: '0.5rem 1rem 1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <h2 style={{ textAlign: 'center', marginTop: '0.75rem', color: '#6e6e6e', marginBottom: '0.75rem' }}>
                        Performance based on themes
                      </h2>

                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#0c5692', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#0c5692' }}>Total Score Assigned </p>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '10px' }}>
                          <div style={{ width: '13px', height: '13px', borderRadius: '30%', backgroundColor: '#96c63c', marginTop: '4px' }}></div>
                          <p style={{ marginLeft: '5px', color: '#96c63c', margin: '0' }}>&nbsp;Total Score Obtained</p>
                        </div>
                      </div>
                      {sktResultDetails?.section_wise_report_theme_details &&
                        sktResultDetails?.section_wise_report_theme_details[0].report_theme_details.length > 0 ? (
                        <SectionWiseChart
                          labelsOfSection={sktResultDetails?.section_wise_report_theme_details[0].report_theme_details.map((item: any) => item.name)}
                          sectionWiseTotal={sktResultDetails?.section_wise_report_theme_details[0].report_theme_details.map(
                            (item: any) => item.markTotal
                          )}
                          sectionWiseTotalObtained={sktResultDetails?.section_wise_report_theme_details[0].report_theme_details.map(
                            (item: any) => item.markScored
                          )}
                          bottomValue={tagBottomValue}
                        />
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </div>
                <div className="scorecard-page-footer">
                  <img src={pageFooter} alt="page footer image" style={{ width: '100%' }} />
                </div>
              </div>

              {/* inside */}
            </>
          )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CentaCandidateReport
